import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, 
  Filter, 
  Clock, 
  Home, 
  TestTubes,
  ChevronRight,
  Droplets,
  Heart,
  Brain,
  Syringe,
  FlaskConical
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestCatalog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [viewMode, setViewMode] = useState('tests'); // 'tests' or 'packages'

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    setSearchParams(params);
  }, [search, selectedCategory]);

  const fetchData = async () => {
    try {
      const [testsRes, packagesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/tests`),
        axios.get(`${API}/packages`),
        axios.get(`${API}/test-categories`)
      ]);
      setTests(testsRes.data);
      setPackages(packagesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = search === '' || 
      test.name.toLowerCase().includes(search.toLowerCase()) ||
      test.code.toLowerCase().includes(search.toLowerCase()) ||
      test.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category) => {
    const icons = {
      blood: Droplets,
      cardiac: Heart,
      hormones: Brain,
      allergy: Syringe,
      urine: TestTubes,
      default: FlaskConical
    };
    return icons[category] || icons.default;
  };

  const getCategoryColor = (category) => {
    const colors = {
      blood: 'bg-red-50 text-red-600 border-red-100',
      cardiac: 'bg-pink-50 text-pink-600 border-pink-100',
      hormones: 'bg-purple-50 text-purple-600 border-purple-100',
      allergy: 'bg-amber-50 text-amber-600 border-amber-100',
      urine: 'bg-blue-50 text-blue-600 border-blue-100',
      imaging: 'bg-cyan-50 text-cyan-600 border-cyan-100',
      pathology: 'bg-orange-50 text-orange-600 border-orange-100',
      genetics: 'bg-indigo-50 text-indigo-600 border-indigo-100',
      default: 'bg-slate-50 text-slate-600 border-slate-100'
    };
    return colors[category] || colors.default;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background" data-testid="test-catalog-page">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-white dark:bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-foreground">
              Lab Test Catalog
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-muted-foreground">
              Browse our comprehensive range of diagnostic tests. Book online and get 
              results delivered digitally.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search tests by name, code, or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 input-clinical"
                data-testid="test-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48 h-12" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Toggle */}
          <div className="mt-6 flex gap-2">
            <Button
              variant={viewMode === 'tests' ? 'default' : 'outline'}
              onClick={() => setViewMode('tests')}
              className={viewMode === 'tests' ? 'bg-sky-600 hover:bg-sky-700' : ''}
              data-testid="view-tests-btn"
            >
              Individual Tests
            </Button>
            <Button
              variant={viewMode === 'packages' ? 'default' : 'outline'}
              onClick={() => setViewMode('packages')}
              className={viewMode === 'packages' ? 'bg-sky-600 hover:bg-sky-700' : ''}
              data-testid="view-packages-btn"
            >
              Health Packages
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : viewMode === 'tests' ? (
          <>
            {/* Category Pills */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-sky-600 text-white' 
                    : 'bg-white dark:bg-card text-slate-600 dark:text-muted-foreground border border-border hover:border-sky-300'
                }`}
                data-testid="category-all-btn"
              >
                All Tests ({tests.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat.value 
                      ? 'bg-sky-600 text-white' 
                      : 'bg-white dark:bg-card text-slate-600 dark:text-muted-foreground border border-border hover:border-sky-300'
                  }`}
                  data-testid={`category-${cat.value}-btn`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Tests Grid */}
            {filteredTests.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-card rounded-xl border border-border">
                <TestTubes className="w-16 h-16 text-slate-300 dark:text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground">No tests found</h3>
                <p className="text-slate-600 dark:text-muted-foreground mt-2">Try adjusting your search or filters</p>
                <Button variant="outline" className="mt-4" onClick={() => { setSearch(''); setSelectedCategory('all'); }}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTests.map((test) => {
                  const Icon = getCategoryIcon(test.category);
                  return (
                    <div 
                      key={test.id} 
                      className="card-clinical test-card cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate(`/tests/${test.id}`)}
                      data-testid={`test-card-${test.id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryColor(test.category)}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <Badge variant="outline" className={getCategoryColor(test.category)}>
                          {test.category}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-foreground mb-2">{test.name}</h3>
                      <p className="text-sm text-slate-600 dark:text-muted-foreground line-clamp-2 mb-4">{test.description}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {test.turnaround_hours}h
                        </span>
                        {test.home_collection_available && (
                          <span className="flex items-center gap-1">
                            <Home className="w-4 h-4" />
                            Home
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <span className="text-xl font-bold text-slate-900 dark:text-foreground">₦{test.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <Button size="sm" className="bg-sky-600 hover:bg-sky-700" data-testid={`book-test-${test.id}-btn`}>
                          Book Now <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          /* Health Packages */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.length === 0 ? (
              <div className="col-span-full text-center py-16 bg-white dark:bg-card rounded-xl border border-border">
                <FlaskConical className="w-16 h-16 text-slate-300 dark:text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground">No packages available</h3>
                <p className="text-slate-600 dark:text-muted-foreground mt-2">Check back later for bundled test packages</p>
              </div>
            ) : (
              packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className="card-clinical border-2 border-border hover:border-sky-300 transition-colors"
                  data-testid={`package-card-${pkg.id}`}
                >
                  <Badge className="mb-4 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800">
                    Save ₦{(pkg.original_price - pkg.discounted_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Badge>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-foreground mb-2">{pkg.name}</h3>
                  <p className="text-slate-600 dark:text-muted-foreground mb-4">{pkg.description}</p>
                  <div className="mb-4">
                    <p className="text-sm text-slate-500 dark:text-muted-foreground mb-2">Includes {pkg.tests?.length || 0} tests</p>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl font-bold text-slate-900 dark:text-foreground">₦{pkg.discounted_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-lg text-slate-400 dark:text-muted-foreground line-through">₦{pkg.original_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <Button 
                    className="w-full bg-sky-600 hover:bg-sky-700"
                    onClick={() => navigate(`/book/package-${pkg.id}`)}
                    data-testid={`book-package-${pkg.id}-btn`}
                  >
                    Book Package
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default TestCatalog;
